import { useState, useRef, useEffect } from 'react';
import { fetchChatHistory, mapNeuronHistoryToUiMessages, pickConversationId } from 'services/travProAgentStream';
import type { Message } from './types';
import { clearPersistedThread, readPersistedThread } from './smartAssistStorage';

export interface UseConversationHistoryProps {
  chatOpen: boolean;
  messages: Message[];
  appId?: number | string | null;
  baseUrl: string;
  getToken: () => Promise<string | null>;
  conversationIdRef: React.MutableRefObject<string | null>;
  msgIdRef: React.MutableRefObject<number>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  resolveEffectiveAppId: () => number | null;
}

export interface UseConversationHistoryReturn {
  historyLoading: boolean;
  triggerHistoryLoad: () => void;
}

export function useConversationHistory({
  chatOpen,
  messages,
  appId,
  baseUrl,
  getToken,
  conversationIdRef,
  msgIdRef,
  setMessages,
  resolveEffectiveAppId,
}: UseConversationHistoryProps): UseConversationHistoryReturn {
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTrigger, setHistoryTrigger] = useState(0);
  const historyLoadGenRef = useRef(0);

  const triggerHistoryLoad = (): void => {
    setHistoryTrigger((n) => n + 1);
  };

  useEffect(() => {
    if (!chatOpen || messages.length > 0) return undefined;

    const aid = resolveEffectiveAppId();
    if (!aid) return undefined;

    const savedConvId = conversationIdRef.current ?? readPersistedThread(String(aid))?.conversationId;
    if (!savedConvId) return undefined;

    conversationIdRef.current = savedConvId;
    const ac = new AbortController();
    const gen = ++historyLoadGenRef.current;

    void (async () => {
      setHistoryLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const data = await fetchChatHistory({
          baseUrl, token, appId: String(aid), conversationId: savedConvId, signal: ac.signal,
        });
        if (gen !== historyLoadGenRef.current || ac.signal.aborted) return;
        const mapped = mapNeuronHistoryToUiMessages(data.messages, data.links);
        conversationIdRef.current = pickConversationId(data) ?? savedConvId;
        msgIdRef.current = mapped.reduce((m, x) => Math.max(m, x.id), 0);
        setMessages(mapped);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (ac.signal.aborted) return;
        clearPersistedThread(String(aid));
        conversationIdRef.current = null;
        if (gen === historyLoadGenRef.current) setMessages([]);
      } finally {
        // Always reset loading for this generation, even on abort (abort = chat closed = no spinner needed)
        if (gen === historyLoadGenRef.current) setHistoryLoading(false);
      }
    })();

    return () => {
      ac.abort();
    };
    // historyTrigger is intentionally included — incrementing it re-fires this effect
    // to load a different conversation while chatOpen=true / messages.length=0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, historyTrigger, messages.length, appId, baseUrl, getToken, resolveEffectiveAppId]);

  return { historyLoading, triggerHistoryLoad };
}
